import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';

function buildEmptyCreateForm() {
  return {
    profileUid: '',
    sourceTicketUid: '',
    category: 'general',
    details: '',
    deadlineAt: '',
  };
}

function buildEmptyResolveForm() {
  return {
    result: 'upheld',
    details: '',
  };
}

function toIsoDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return null;
  }

  return new Date(`${raw}T00:00:00.000Z`).toISOString();
}

export function useComplianceComplaints() {
  const { token } = useAuth();
  const [report, setReport] = useState({
    items: [],
    openCount: 0,
    resolvedCount: 0,
    overdueCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeActionUid, setActiveActionUid] = useState('');
  const [createForm, setCreateForm] = useState(buildEmptyCreateForm());
  const [resolveModal, setResolveModal] = useState({
    isOpen: false,
    uid: '',
    isSubmitting: false,
    form: buildEmptyResolveForm(),
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getComplaintReport(token);
      setReport({
        items: Array.isArray(response?.items) ? response.items : [],
        openCount: Number(response?.openCount ?? 0),
        resolvedCount: Number(response?.resolvedCount ?? 0),
        overdueCount: Number(response?.overdueCount ?? 0),
      });
    } catch (requestError) {
      setReport({
        items: [],
        openCount: 0,
        resolvedCount: 0,
        overdueCount: 0,
      });
      setError(requestError.message || 'Failed to load complaint report');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreateFormChange = useCallback((key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const submitCreate = useCallback(async (event) => {
    event.preventDefault();

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.createComplaint(token, {
        profileUid: String(createForm.profileUid || '').trim() || null,
        sourceTicketUid: String(createForm.sourceTicketUid || '').trim() || null,
        category: String(createForm.category || '').trim() || null,
        details: String(createForm.details || '').trim() || null,
        deadlineAt: toIsoDateTime(createForm.deadlineAt),
      });
      setCreateForm(buildEmptyCreateForm());
      setSuccess('Complaint created.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to create complaint');
    } finally {
      setIsSubmitting(false);
    }
  }, [createForm.category, createForm.deadlineAt, createForm.details, createForm.profileUid, createForm.sourceTicketUid, load, token]);

  const performAction = useCallback(async (uid, action) => {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      return;
    }

    setActiveActionUid(normalizedUid);
    setError('');
    setSuccess('');

    try {
      if (action === 'acknowledge') {
        await adminApi.acknowledgeComplaint(token, normalizedUid);
        setSuccess('Complaint acknowledged.');
      } else if (action === 'holding') {
        await adminApi.sendComplaintHoldingResponse(token, normalizedUid);
        setSuccess('Holding response sent.');
      }
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to update complaint');
    } finally {
      setActiveActionUid('');
    }
  }, [load, token]);

  const openResolve = useCallback((uid) => {
    setResolveModal({
      isOpen: true,
      uid: String(uid || '').trim(),
      isSubmitting: false,
      form: buildEmptyResolveForm(),
    });
  }, []);

  const closeResolve = useCallback(() => {
    setResolveModal({
      isOpen: false,
      uid: '',
      isSubmitting: false,
      form: buildEmptyResolveForm(),
    });
  }, []);

  const onResolveFormChange = useCallback((key, value) => {
    setResolveModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [key]: value,
      },
    }));
  }, []);

  const submitResolve = useCallback(async (event) => {
    event.preventDefault();

    if (!resolveModal.uid) {
      return;
    }

    setResolveModal((prev) => ({ ...prev, isSubmitting: true }));
    setError('');
    setSuccess('');

    try {
      await adminApi.resolveComplaint(token, resolveModal.uid, {
        result: String(resolveModal.form.result || '').trim() || null,
        details: String(resolveModal.form.details || '').trim() || null,
      });
      setSuccess('Complaint resolved.');
      closeResolve();
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to resolve complaint');
      setResolveModal((prev) => ({ ...prev, isSubmitting: false }));
    }
  }, [closeResolve, load, resolveModal.form.details, resolveModal.form.result, resolveModal.uid, token]);

  return {
    report,
    isLoading,
    isSubmitting,
    activeActionUid,
    createForm,
    resolveModal,
    error,
    success,
    onCreateFormChange,
    submitCreate,
    acknowledgeComplaint: (uid) => performAction(uid, 'acknowledge'),
    sendHoldingResponse: (uid) => performAction(uid, 'holding'),
    openResolve,
    closeResolve,
    onResolveFormChange,
    submitResolve,
  };
}
